<template>
  <div ref="consolePopupGroup" class="flex items-center btn-group-border">
    <div class="tooltip w-full" :data-tip="compileButtonTooltip">
      <button
        @click="firmware.compile"
        class="btn btn-neutral btn-group-start min-w-[12rem] w-full"
        :disabled="
          backend.currentConfig == null ||
          backend.configModified ||
          compileButtonText != 'Compile'
        "
      >
        <span v-if="compileButtonText != 'Compile'" class="loading"></span>
        {{ compileButtonText }}
      </button>
    </div>

    <div
      class="tooltip indicator"
      data-tip="Open console with compilation output"
    >
      <span
        v-show="consoleUnread > 0"
        class="indicator-item badge badge-primary px-1.5"
      >
        {{ consoleUnread < 100 ? consoleUnread.toString() : "99+" }}
      </span>
      <button
        ref="consoleButton"
        class="btn btn-neutral btn-group-end"
        @click="consoleOpen = !consoleOpen"
      >
        <Icon icon="mdi:console" class="text-xl" />
      </button>
    </div>

    <PopUp
      :show="consoleOpen"
      :reference="consoleButton"
      placement="top"
      :offset="8"
    >
      <div class="p-1 indicator">
        <span
          v-if="!firmware.consoleActive"
          class="indicator-item indicator-middle indicator-center"
        >
          <div
            class="-translate-x-1/2 -translate-y-1/2 text-base-100 min-w-max text-center text-xl"
          >
            Not connected to server
          </div>
        </span>

        <ConsoleOutput
          ref="consoleOutput"
          class="w-[520px] h-[520px] md:w-[760px] md:h-[420px]"
          :class="firmware.consoleActive ? '' : 'opacity-60'"
        />
      </div>
    </PopUp>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { err } from "@/utils";
import { useBackendStore, useFirmwareStore } from "@/stores";
import { useOnClickOutside } from "@/composables";
import PopUp from "@/components/utils/PopUp.vue";
import ConsoleOutput from "@/components/ConsoleOutput.vue";

const backend = useBackendStore();
const firmware = useFirmwareStore();

const consolePopupGroup = ref<HTMLElement | null>(null);
const consoleOutput = ref<InstanceType<typeof ConsoleOutput> | null>(null);
const consoleButton = ref<HTMLElement | null>(null);
const consoleOpen = ref(false);
const consoleUnread = ref(0);

// Close popup when clicking outside
useOnClickOutside(consolePopupGroup, () => (consoleOpen.value = false));

// Keep track of unread messages
watch(consoleOpen, (open) => {
  if (open) {
    consoleUnread.value = 0;
    setTimeout(() => consoleOutput.value?.scroll());
  }
});
watch(
  () => firmware.consoleOutputCount,
  (newCount, oldCount) => {
    if (newCount == 0) consoleUnread.value = 0;
    else if (!consoleOpen.value) {
      const diff = newCount - oldCount;
      consoleUnread.value += Math.max(0, diff);
    }
  }
);

const compileButtonText = computed(() =>
  firmware.buildStatus == "PENDING"
    ? "Pending…"
    : firmware.buildStatus == "RUNNING"
    ? "Compiling…"
    : "Compile"
);

const compileButtonTooltip = computed(() => {
  if (backend.currentConfig == null || backend.configModified)
    return err("Configuration changes must first be saved");
  return "Compile keyboard firmware using current configuration";
});
</script>
